<!-- 复用的卡片组件，用于展示面经信息的单个列表项 -->

<!-- 定义组件模板 -->
<template>
    <!-- 面经列表项组件，修复布局问题，点击该卡片会触发事件 -->
    <div class="article-info-card" @click="handleClick">
        <!-- 标题区域，显示文章的标题 -->
        <h3 class="article-title">{{ article.title }}</h3>

        <!-- 摘要区域，确保以3行的形式显示文章摘要，超出部分隐藏 -->
        <p class="article-summary">{{ article.summary }}</p>

        <!-- 元数据区域，显示文章的浏览量、点赞数、评论数和发布时间 -->
        <div class="article-meta">
            <!-- 显示文章的浏览量，使用眼睛图标作为标识 -->
            <span class="meta-item">
                <i class="bi bi-eye"></i>
                <!-- 对浏览量进行格式化处理后显示 -->
                {{ formatNumber(article.views) }}
            </span>
            <!-- 点赞按钮，根据是否已点赞切换样式，点击时阻止事件冒泡 -->
            <span class="meta-item like-btn" :class="{ active: isLiked }" @click.stop="handleLike">
                <i :class="['bi', isLiked ? 'bi-heart-fill' : 'bi-heart']"></i>
                <!-- 对点赞数进行格式化处理后显示 -->
                {{ formatNumber(article.likes) }}
            </span>
            <!-- 显示文章的评论数，使用聊天图标作为标识 -->
            <span class="meta-item">
                <i class="bi bi-chat"></i>
                <!-- 对评论数进行格式化处理后显示 -->
                {{ formatNumber(article.comments) }}
            </span>
            <!-- 显示文章的发布时间，使用特定格式处理时间 -->
            <span class="meta-item time">
                {{ formatTime(article.publishTime) }}
            </span>
        </div>

        <!-- 收藏按钮，调整位置到卡片右上角，点击时阻止事件冒泡 -->
        <button class="collect-btn" :class="{ collected: isCollected }" @click.stop="handleCollect"
            :title="isCollected ? '取消收藏' : '收藏'">
            <!-- 根据是否已收藏切换图标 -->
            <i :class="['bi', isCollected ? 'bi-bookmark-fill' : 'bi-bookmark']"></i>
        </button>

        <!-- 标签区域，仅当文章存在标签时显示 -->
        <div class="article-tags" v-if="article.tags && article.tags.length">
            <!-- 遍历文章标签数组，为每个标签创建一个标签元素 -->
            <span v-for="tag in article.tags" :key="tag" class="tag">
                {{ tag }}
            </span>
        </div>
    </div>
</template>

<!-- 定义组件逻辑 -->
<script>
// 逻辑代码保持不变
export default {
    // 组件名称，方便在其他地方引用和调试
    name: 'ArticleInfo',
    // 定义组件接收的属性
    props: {
        // 文章对象，包含文章的各种信息，为必需属性
        article: {
            type: Object,
            required: true
        }
    },
    // 计算属性，用于返回派生状态
    computed: {
        // 判断文章是否已点赞，当前暂时返回false，后续应从store获取
        isLiked() {
            // 这里应该从store获取，暂时返回false
            return false
        },
        // 判断文章是否已收藏，当前暂时返回false，后续应从store获取
        isCollected() {
            // 这里应该从store获取，暂时返回false
            return false
        }
    },
    // 定义组件方法
    methods: {
        // 处理卡片点击事件，触发自定义事件并传递文章ID
        handleClick() {
            this.$emit('article-click', this.article.id)
        },
        // 处理点赞事件，触发自定义事件并传递文章ID和新的点赞状态
        handleLike() {
            this.$emit('like-change', this.article.id, !this.isLiked)
        },
        // 处理收藏事件，触发自定义事件并传递文章ID和新的收藏状态
        handleCollect() {
            this.$emit('collect-change', this.article.id, !this.isCollected)
        },
        // 对数字进行格式化处理，当数字大于等于1000时，转换为以k为单位
        formatNumber(num) {
            if (num >= 1000) {
                return (num / 1000).toFixed(1) + 'k'
            }
            return num.toString()
        },
        // 对时间进行格式化处理，根据与当前时间的差值返回不同格式的时间描述
        formatTime(date) {
            // 获取当前时间
            const now = new Date()
            // 计算当前时间与文章发布时间的差值（毫秒）
            const diff = now - new Date(date)
            // 计算差值对应的小时数
            const hours = Math.floor(diff / (1000 * 60 * 60))
            if (hours < 1) return '刚刚'
            if (hours < 24) return `${hours}小时前`
            if (hours < 168) return `${Math.floor(hours / 24)}天前`

            // 若时间差值较大，返回格式化后的日期
            return new Date(date).toLocaleDateString('zh-CN', {
                month: 'short',
                day: 'numeric'
            })
        }
    }
}
</script>

<!-- 定义组件样式，scoped表示样式仅作用于当前组件 -->
<style scoped>
/* 修复后的样式 */
/* 面经列表项卡片样式 */
/* 使用新盒子模型，让内边距和边框包含在元素的总宽度和高度内 */
.article-info-card {
    box-sizing: border-box;
    /* 设置卡片背景颜色为白色 */
    background: white;
    /* 设置卡片圆角为12px */
    border-radius: 12px;
    /* 设置卡片内边距为20px */
    padding: 20px;
    /* 设置卡片底部外边距为15px */
    margin-bottom: 22px;
    /* 设置卡片阴影效果 */
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.288);
    /* 设置过渡效果，使卡片状态变化更平滑 */
    transition: all 0.25s ease;
    /* 设置鼠标指针样式为手型，表示可点击 */
    cursor: pointer;
    /* 设置卡片边框样式 */
    border: 1px solid #f0f0f0;
    /* 设置定位为相对定位，为收藏按钮提供定位上下文 */
    position: relative;
    /* 为收藏按钮提供定位上下文 */
}

/* 鼠标悬停在卡片上时的样式 */
.article-info-card:hover {
    /* 鼠标悬停在卡片上时，卡片放大并调整阴影效果的样式 */
    transform: scale(1.025);
    /* 增大卡片阴影效果 */
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

/* 文章标题样式 */
.article-title {
    /* 设置标题颜色 */
    color: #1262ad;
    /* 设置标题字体大小 */
    font-size: 1.55rem;
    /* 设置标题字体粗细 */
    font-weight: 600;
    /* 设置标题行高 */
    line-height: 1.4;
    /* 设置标题底部外边距 */
    margin-bottom: 10px;
    /* 为收藏按钮留出空间 */
    padding-right: 30px;
    /* 为收藏按钮留出空间 */
}

/* 文章摘要样式 —— 现代 Flexbox 实现 3 行溢出隐藏 */
.article-summary {
    color: #666;
    font-size: 0.9rem;
    line-height: 2em;
    margin-bottom: 15px;
    /* 1. 用 flex 布局替代 -webkit-box */
    display: flex;
    flex-direction: column;
    /* 垂直排列 */
    justify-content: flex-start;
    /* 顶部对齐 */
    /* 2. 限制高度 = 3 行文本高度（line-height × 3） */
    max-height: calc(2em * 3);
    /* 2 是 line-height 的值 */
    overflow: hidden;
    /* 3. 防止单词内断行，确保截断在单词边界 */
    word-break: break-word;
    /* 4. 给收藏按钮留空间 */
    padding-right: 30px;
}

/* 文章元数据区域样式 */
.article-meta {
    /* 使用flex布局 */
    display: flex;
    justify-content: start;
    /* 设置子元素之间的间距为20px */
    gap: 1.6rem;
    /* 设置元数据字体大小 */
    font-size: 0.88rem;
    /* 设置元数据颜色 */
    color: #040291;
    /* 设置元数据区域底部外边距 */
    margin-bottom: 0.8rem;

}

/* 元数据项样式 */
.meta-item {
    /* 使用flex布局 */
    display: flex;
    /* 设置子元素之间的间距为4px */
    gap: 0.44rem;
}

/* 元数据项中的图标样式 */
.meta-item i {
    /* 设置图标字体大小 */
    font-size: 0.88rem;

}

/* 点赞按钮激活状态样式 */
.like-btn.active {
    /* 设置激活状态的颜色 */
    color: #ff4757;
}

/* 收藏按钮样式 */
.collect-btn {
    /* 设置绝对定位，相对于父元素(.article-info-card)定位 */
    position: absolute;
    /* 设置距离顶部15px */
    top: 15px;
    /* 设置距离右侧15px */
    right: 15px;
    /* 去除按钮背景 */
    background: none;
    /* 去除按钮边框 */
    border: none;
    /* 设置按钮颜色 */
    color: #999;
    /* 设置鼠标指针样式为手型 */
    cursor: pointer;
    /* 设置按钮内边距 */
    padding: 8px;
    /* 设置按钮圆角为50%，使其成为圆形 */
    border-radius: 50%;
    /* 设置过渡效果，使按钮状态变化更平滑 */
    transition: all 0.3s ease;
    /* 设置按钮字体大小 */
    font-size: 18px;
}

/* 鼠标悬停在收藏按钮上时的样式 */
.collect-btn:hover {
    /* 设置悬停时的背景颜色 */
    background: #f0f0f0;
    /* 设置悬停时的文字颜色 */
    color: #333;
}

/* 收藏按钮已收藏状态样式 */
.collect-btn.collected {
    /* 设置已收藏状态的颜色 */
    color: #ffa502;
}

/* 文章标签区域样式 */
.article-tags {
    /* 设置标签区域顶部外边距 */
    margin-top: 12px;
    /* 使用flex布局 */
    display: flex;
    /* 设置子元素之间的间距为8px */
    gap: 8px;
    /* 允许子元素换行 */
    flex-wrap: wrap;
}

/* 标签样式 */
.tag {
    /* 设置标签背景颜色 */
    background: #f0f7ff;
    /* 设置标签文字颜色 */
    color: #1890ff;
    /* 设置标签内边距 */
    padding: 4px 8px;
    /* 设置标签圆角 */
    border-radius: 4px;
    /* 设置标签字体大小 */
    font-size: 12px;
}
</style>